import { Camera, PerspectiveCamera, Scene, WebGLRenderer } from '@seddi/three';
import type { SceneRenderable } from './renderables/sceneRenderable';
import { create as createRenderable } from './renderables/sceneRenderable';
import { DefaultRenderingPipeline } from './renderer3/defaultRenderingPipeline';
import type { ResourcesContext } from './resources/resourcesContext';
import type { SceneDesc } from './scene/sceneTypes';
import { SceneReconciler } from './sceneReconciler/sceneReconciler';
import { themeDefault } from './theme/themeDefault';
import { type SceneViewerConfig, create as createConfig } from './sceneViewerConfig';

const Visible = {
  avatar: false,
  background: false,
  garmentHem: true,
  garmentPiece: true,
  garmentSeam: true,
  ground: false,
  logos: true,
};

export type SceneViewerContext = {
  camera: Camera;
  readonly config: SceneViewerConfig;
  isDisposed: boolean;
  needsUpdate: boolean;
  readonly pipeline: DefaultRenderingPipeline;
  readonly resources: ResourcesContext;
  readonly scene: Scene;
  state?: SceneDesc;
  readonly sceneReconciler: SceneReconciler;
  readonly sceneRenderable: SceneRenderable;
};

export const create = (
  config: Partial<SceneViewerConfig>,
  renderer: WebGLRenderer,
  resources: ResourcesContext,
): SceneViewerContext => {
  const camera = new PerspectiveCamera(20, 1, 1, 10);
  camera.position.y = 1.1;
  camera.position.z = 2;
  const config_ = createConfig(config);
  const scene = new Scene();
  const sceneRenderable = createRenderable(scene);
  const sceneReconciler = new SceneReconciler(resources, sceneRenderable, config_, themeDefault);

  // Render pipeline
  const pipeline = new DefaultRenderingPipeline(renderer);
  pipeline.setCamera(camera);
  pipeline.setScene(scene);

  return {
    camera,
    config: config_,
    isDisposed: false,
    needsUpdate: true,
    pipeline,
    resources,
    scene,
    sceneReconciler,
    sceneRenderable,
  };
};

export const dispose = (self: SceneViewerContext) => {
  self.pipeline.dispose();
  self.sceneReconciler.dispose();
  self.isDisposed = true;
};

export const setSize = (self: SceneViewerContext, width: number, height: number) => {
  const devicePixelRatio = window.devicePixelRatio;
  // Update render pipeline targets
  const pixelWidth = Math.round(width * devicePixelRatio);
  const pixelHeight = Math.round(height * devicePixelRatio);
  self.pipeline.setSize(pixelWidth, pixelHeight);
  self.needsUpdate = true;
};

export const setState = (self: SceneViewerContext, state: SceneDesc) => {
  self.state = state;
};

export const reconcile = (self: SceneViewerContext) => {
  if (!self.state) {
    return;
  }

  // TODO: Configure showGarmentGeometry before update.
  self.sceneReconciler.update(self.state, true);
  self.sceneReconciler.updateVisible(Visible, [], true);

  if (self.sceneReconciler.needsUpdate) {
    self.needsUpdate = true;
    self.sceneReconciler.needsUpdate = false;
  }
};

export const render = (self: SceneViewerContext, deltaTime?: number) => {
  self.pipeline.render(deltaTime);
};
